import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  message,
  Tag,
  Tooltip,
  Table,
  Button,
  Modal,
  InputNumber,
  Form,
} from "antd";
import { SubscriptionData } from "../pricing_page/Subscription";
import {
  FaCheck,
  FaCrown,
  FaInfoCircle,
  FaArrowUp,
  FaArrowDown,
  FaDownload,
  FaTimes,
} from "react-icons/fa";
import { CustomButton } from "../physician_page/styled";
import SubscribeSuccessfulModal from "../pricing_page/SubscribeSuccessfulModal";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import subscriptionService from "../../../services/subscriptioin";
import { notify } from "../../../utils/helper";
import ModelWrapper from "../../modal/ModelWrapper";

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(138, 62, 244, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(138, 62, 244, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(138, 62, 244, 0);
  }
`;

const scrollText = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
`;

const MySubscriptionPageWrapper = styled.div`
  padding: 40px 60px;

  --success-color: #10b981;
  --success-color-hover: #059669;
  --success-color-dark: #047857;
  --error-color: #ef4444;
  --error-color-hover: #dc2626;
  --error-color-dark: #b91c1c;
  --primary-gradient: linear-gradient(135deg, #8a3ef4 0%, #6b2fd9 100%);
  --success-gradient: linear-gradient(135deg, #10b981 0%, #059669 100%);
  --error-gradient: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  --border-color-rgb: 226, 232, 240;
  --text-accent-color-rgb: 138, 62, 244;

  .ant-card {
    border: 1px solid var(--border-color);
    border-radius: 20px;
    background: var(--primary-bg) !important;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    height: 100%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(20px);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: var(--primary-gradient);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 20px 40px rgba(138, 62, 244, 0.15);
      border-color: rgba(138, 62, 244, 0.3);

      &::before {
        opacity: 1;
      }

      .plan-icon {
        transform: scale(1.1) rotate(5deg);
        background: var(--primary-gradient);
        color: white;
      }
    }

    &.active-plan {
      border: 2px solid var(--text-accent-color);
      background: linear-gradient(
        135deg,
        rgba(138, 62, 244, 0.08) 0%,
        rgba(138, 62, 244, 0.02) 100%
      ) !important;
      transform: scale(1.05);
      box-shadow: 0 12px 32px rgba(138, 62, 244, 0.2);

      &::before {
        opacity: 1;
        height: 6px;
      }

      .plan-header {
        background: linear-gradient(
          135deg,
          rgba(138, 62, 244, 0.15) 0%,
          rgba(138, 62, 244, 0.08) 100%
        );
      }

      .plan-icon {
        background: var(--primary-gradient);
        color: white;
        transform: scale(1.1);
        box-shadow: 0 8px 20px rgba(138, 62, 244, 0.3);
      }

      .active-badge {
        position: absolute;
        top: 16px;
        right: 16px;
        background: var(--success-gradient);
        color: white;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 11px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 4px;
        z-index: 10;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      }
    }

    &.upgrade-plan {
      border: 1px solid rgba(16, 185, 129, 0.3);

      &:hover {
        border-color: var(--success-color);
        box-shadow: 0 20px 40px rgba(16, 185, 129, 0.15);
      }

      .plan-action-button.upgrade {
        background: var(--success-gradient);
        border-color: var(--success-color);

        &:hover {
          background: linear-gradient(
            135deg,
            var(--success-color-hover) 0%,
            var(--success-color-dark) 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
        }
      }
    }

    &.downgrade-plan {
      border: 1px solid rgba(239, 68, 68, 0.3);

      &:hover {
        border-color: var(--error-color);
        box-shadow: 0 20px 40px rgba(239, 68, 68, 0.15);
      }

      .plan-action-button.downgrade {
        background: var(--error-gradient);
        border-color: var(--error-color);

        &:hover {
          background: linear-gradient(
            135deg,
            var(--error-color-hover) 0%,
            var(--error-color-dark) 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }
      }
    }
  }

  .ant-card-body {
    height: 100%;
    padding: 0 !important;
    display: flex;
    flex-direction: column;
  }

  .plan-header {
    text-align: left;
    padding: 20px 24px;
    position: relative;
    background: var(--secondary-bg);
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 0;
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    overflow: hidden;
    border-radius: 20px 20px 0 0;

    &:hover {
      .plan-description {
        animation: ${scrollText} 15s linear infinite;
        white-space: nowrap;
        text-overflow: clip;
        overflow: visible;
        position: relative;
        z-index: 1;
      }
    }
  }

  .plan-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--secondary-bg);
    border-radius: 16px;
    color: var(--text-accent-color);
    font-size: 20px;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    border: 2px solid var(--border-color);

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.2) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      border-radius: 16px;
    }
  }

  .plan-header-content {
    flex-grow: 1;
    min-width: 0;
    overflow: hidden;
    position: relative;
  }

  .plan-name {
    font-size: 18px;
    font-weight: 700;
    color: var(--primary-text-color);
    margin-bottom: 4px;
    letter-spacing: -0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .plan-description {
    font-size: 13px;
    color: var(--secondary-text-color);
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    transition: all 0.3s ease;
    position: relative;
    display: inline-block;
    opacity: 0.8;
  }

  .plan-content {
    padding: 24px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .price-expiry-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
  }

  .plan-price {
    font-size: 36px;
    font-weight: 800;
    color: var(--primary-text-color);
    line-height: 1;
    margin-bottom: 0;
    background: linear-gradient(135deg, var(--text-accent-color), #6b2fd9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    flex: 1;

    .billing-period {
      font-size: 16px;
      font-weight: 500;
      color: var(--secondary-text-color);
      margin-left: 4px;
    }
  }

  .plan-features {
    list-style: none;
    padding: 0;
    margin: 0;
    flex-grow: 1;

    li {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      font-size: 14px;
      color: var(--primary-text-color);
      /* border-bottom: 1px solid rgba(var(--border-color-rgb), 0.1); */
      border-bottom: 1px solid var(--border-muted-color);
      transition: all 0.2s ease;

      &:hover {
        background: rgba(var(--text-accent-color-rgb), 0.2);
        padding-left: 8px;
        border-radius: 8px;
      }

      &:last-child {
        border-bottom: none;
      }

      svg {
        color: #10b981;
        font-size: 12px;
        flex-shrink: 0;
      }
    }
  }

  .plan-action-button {
    width: 100%;
    padding: 14px 24px;
    border: 2px solid var(--text-accent-color);
    border-radius: 12px;
    background: transparent;
    color: var(--text-accent-color);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: auto;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(var(--text-accent-color-rgb), 0.3);
    }

    &.upgrade {
      background: var(--success-gradient);
      border-color: var(--success-color);
      color: white;

      &:hover {
        background: linear-gradient(
          135deg,
          var(--success-color-hover) 0%,
          var(--success-color-dark) 100%
        );
        box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
      }
    }

    &.downgrade {
      background: var(--error-gradient);
      border-color: var(--error-color);
      color: white;

      &:hover {
        background: linear-gradient(
          135deg,
          var(--error-color-hover) 0%,
          var(--error-color-dark) 100%
        );
        box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
      }
    }

    &.current {
      background: var(--primary-gradient);
      border-color: var(--text-accent-color);
      color: white;
      cursor: default;

      &:hover {
        transform: none;
        box-shadow: 0 4px 12px rgba(138, 62, 244, 0.3);
      }
    }
  }

  .expiry-date {
    background: linear-gradient(
      135deg,
      rgba(138, 62, 244, 0.1) 0%,
      rgba(138, 62, 244, 0.05) 100%
    );
    color: var(--text-accent-color);
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    border: 1px solid rgba(138, 62, 244, 0.2);
    margin-bottom: 0;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .physician-count {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 8px 12px;
    border-radius: 8px;
    margin: 12px 0;
    text-align: center;
    font-size: 13px;
    color: #475569;
    border: 1px solid #e2e8f0;
  }

  .cancel-subscription-modal {
    .ant-modal-content {
      background: var(--primary-bg) !important;
      border: 1px solid var(--border-color);
      border-radius: 16px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }

    .ant-modal-header {
      background: var(--primary-bg) !important;
      border-bottom: 1px solid var(--border-color);
      border-radius: 16px 16px 0 0;
      padding: 20px 24px;

      .ant-modal-title {
        color: var(--primary-text-color) !important;
        font-weight: 600;
        font-size: 18px;
      }
    }

    .ant-modal-body {
      background: var(--primary-bg) !important;
      padding: 24px;
    }

    .ant-modal-footer {
      background: var(--primary-bg) !important;
      border-top: 1px solid var(--border-color);
      border-radius: 0 0 16px 16px;
      padding: 16px 24px;

      .ant-btn {
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-1px);
        }
      }
    }

    .modal-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      text-align: center;
    }

    .warning-icon {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: linear-gradient(
        135deg,
        rgba(239, 68, 68, 0.1) 0%,
        rgba(239, 68, 68, 0.05) 100%
      );
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid rgba(239, 68, 68, 0.2);
    }

    .modal-message {
      color: var(--primary-text-color);
      font-size: 15px;
      line-height: 1.6;
      margin: 0;

      strong {
        color: var(--error-color);
        font-weight: 600;
      }
    }
  }

  .confirm-subscription-modal {
    .ant-modal-content {
      background: var(--primary-bg) !important;
      border: 1px solid var(--border-color);
      border-radius: 16px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }

    .ant-modal-header {
      background: var(--primary-bg) !important;
      border-bottom: 1px solid var(--border-color);
      border-radius: 16px 16px 0 0;
      padding: 20px 24px;

      .ant-modal-title {
        color: var(--primary-text-color) !important;
        font-weight: 600;
        font-size: 18px;
      }
    }

    .ant-modal-body {
      background: var(--primary-bg) !important;
      padding: 24px;
    }

    .ant-modal-footer {
      background: var(--primary-bg) !important;
      border-top: 1px solid var(--border-color);
      border-radius: 0 0 16px 16px;
      padding: 16px 24px;

      .ant-btn {
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-1px);
        }
      }
    }

    .modal-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      text-align: center;
    }

    .confirm-icon {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid;
    }

    .modal-message {
      color: var(--primary-text-color);
      font-size: 15px;
      line-height: 1.6;
      margin: 0;

      strong {
        font-weight: 600;
      }
    }
  }
`;

const MainHeading = styled.div`
  font-size: 46px;
  color: var(--primary-text-color);
  text-align: center;
`;
const SubHeading = styled.div`
  font-size: 21px;
  color: var(--secondary-text-color);
  width: 55%;
  text-align: center;
`;

const InvoiceTableWrapper = styled.div`
  margin-top: 60px;
  padding: 0 20px;

  .ant-table-wrapper {
    background: var(--primary-bg);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px var(--shadow-color);
  }

  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: var(--secondary-bg);
    color: var(--primary-text-color);
    font-weight: 600;
    border-bottom: 1px solid var(--border-color);
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--border-color);
    color: var(--primary-text-color);
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(138, 62, 244, 0.02);
  }

  /* Pagination Styles */
  .ant-pagination {
    margin: 16px 0;
    padding: 0 16px;
    color: var(--primary-text-color);
  }

  .ant-pagination-item {
    background: var(--primary-bg);
    border-color: var(--border-color);

    a {
      color: var(--primary-text-color);
    }

    &:hover {
      border-color: var(--text-accent-color);
      a {
        color: var(--text-accent-color);
      }
    }
  }

  .ant-pagination-item-active {
    border-color: var(--primary-bg);
    background: var(--primary-bg);

    a {
      color: white;
    }

    &:hover {
      border-color: var(--text-accent-color);
      a {
        color: white;
      }
    }
  }

  .ant-pagination-prev,
  .ant-pagination-next {
    .ant-pagination-item-link {
      background: var(--primary-bg);
      border-color: var(--border-color);
      color: var(--primary-text-color);
    }

    &:hover .ant-pagination-item-link {
      border-color: var(--text-accent-color);
      color: var(--text-accent-color);
    }
  }

  .ant-pagination-disabled {
    .ant-pagination-item-link {
      background: var(--secondary-bg);
      border-color: var(--border-color);
      color: var(--secondary-text-color);
    }

    &:hover .ant-pagination-item-link {
      border-color: var(--border-color);
      color: var(--secondary-text-color);
    }
  }

  .ant-pagination-options {
    .ant-select-selector {
      background: var(--primary-bg) !important;
      border-color: var(--border-color) !important;
      color: var(--primary-text-color) !important;
    }

    .ant-select-arrow {
      color: var(--primary-text-color);
    }
  }

  .ant-pagination-options-quick-jumper {
    color: var(--primary-text-color);

    input {
      background: var(--primary-bg) !important;
      border-color: var(--border-color) !important;
      color: var(--primary-text-color) !important;
    }
  }

  .ant-select-dropdown {
    background: var(--primary-bg);
    border: 1px solid var(--border-color);
    box-shadow: 0 2px 8px var(--shadow-color);

    .ant-select-item {
      color: var(--primary-text-color);

      &:hover {
        background: var(--hover-bg);
      }

      &-option-selected {
        background: var(--active-bg);
        color: var(--text-accent-color);
      }
    }
  }

  .download-button {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text-accent-color);
    background: rgba(138, 62, 244, 0.1);
    border: none;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 13px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(138, 62, 244, 0.15);
      color: #6b2fd9;
    }
  }

  .invoice-status {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &.paid {
      background: rgba(34, 197, 94, 0.1);
      color: #16a34a;
    }

    &.unpaid {
      background: rgba(239, 68, 68, 0.1);
      color: #dc2626;
    }

    &.open {
      background: rgba(234, 179, 8, 0.1);
      color: #ca8a04;
    }
  }

  .pay-now-button {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--text-accent-color);
    border: none;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 13px;
    transition: all 0.2s ease;
    color: white;

    &:hover {
      background: #6b2fd9;
      border-color: #6b2fd9;
    }
  }
`;

const QuantityModal = ({
  isOpen,
  onClose,
  onSubmit,
  type,
  currentQuantity = 1,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      onSubmit(values.quantity);
      form.resetFields();
      onClose();
    });
  };

  return (
    <ModelWrapper
      title={`${type === "upgrade" ? "Upgrade" : "Downgrade"} Subscription`}
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={type === "upgrade" ? "Upgrade" : "Downgrade"}
      cancelText="Cancel"
      okButtonProps={{
        style: {
          background: "#8a3ef4",
          borderColor: "#8a3ef4",
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ quantity: currentQuantity }}
      >
        <Form.Item
          label="Number of Users"
          name="quantity"
          rules={[
            { required: true, message: "Please enter the number of users" },
            { type: "number", min: 1, message: "Minimum quantity is 1" },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={1}
            placeholder="Enter number of users"
          />
        </Form.Item>
      </Form>
    </ModelWrapper>
  );
};

const MySubscription = () => {
  const [subscription, setSubscription] = useState(SubscriptionData?.plans);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [licenseLimit, setLicenseLimit] = useState(null);
  const [isQuantityModalOpen, setIsQuantityModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [modalType, setModalType] = useState("upgrade");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const history = useHistory();

  const planHierarchy = {
    Standard: 1,
    Professional: 2,
    Group: 3,
    Enterprise: 4,
  };

  const getPlanLevel = (planName) => {
    return planHierarchy[planName] || 0;
  };

  const fetchSubscriptionDetails = async () => {
    try {
      const response = await subscriptionService.getSubscriptionDetails();
      setSubscriptionDetails(response.data);
      // console.log("details:", response?.data);
    } catch (error) {
      notify("error", "Failed to fetch subscription details");
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await subscriptionService.getSubscriptionInvoices();
      setInvoices(response.data);
      // console.log(response.data);
    } catch (error) {
      notify("error", "Failed to fetch invoices");
    }
  };

  const checkLicenseLimit = async () => {
    try {
      const response = await subscriptionService.checkLicenseLimit();
      setLicenseLimit(response.data);
    } catch (error) {
      notify("error", "Failed to check license limit");
    }
  };

  const handleUpgradeSubscription = async (planId, quantity) => {
    try {
      await subscriptionService.upgradeSubscription({
        newPriceId: planId,
        quantity: quantity,
      });
      notify("success", "Subscription upgraded successfully");
      fetchSubscriptionDetails();
    } catch (error) {
      notify("error", "Failed to upgrade subscription");
    }
  };

  const handleDowngradeSubscription = async (planId, quantity) => {
    try {
      await subscriptionService.downgradeSubscription({
        newPriceId: planId,
        quantity: quantity,
      });
      notify("success", "Subscription downgraded successfully");
      fetchInvoices();
      fetchSubscriptionDetails();
    } catch (error) {
      notify("error", "Failed to downgrade subscription");
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await subscriptionService.cancelSubscription();
      notify("success", "Subscription cancelled successfully");
      fetchSubscriptionDetails();
    } catch (error) {
      notify("error", "Failed to cancel subscription");
    }
  };

  const getPlanStatus = (priceId) => {
    if (!subscriptionDetails) return null;
    if (subscriptionDetails.priceId === priceId) {
      return {
        type: "active",
        text: "Current Plan",
        color: "#8a3ef4",
      };
    }
    return null;
  };

  const openQuantityModal = (plan, type) => {
    setSelectedPlan(plan);
    setModalType(type);
    setIsQuantityModalOpen(true);
  };

  const openConfirmModal = (plan, type) => {
    setSelectedPlan(plan);
    setModalType(type);
    setIsConfirmModalOpen(true);
  };

  const handlePlanAction = (plan, type) => {
    if (plan.name === "Group") {
      openQuantityModal(plan, type);
    } else {
      openConfirmModal(plan, type);
    }
  };

  const handleQuantitySubmit = (quantity) => {
    if (!selectedPlan) return;
    // console.log("selectedPlan:", selectedPlan, quantity);

    if (modalType === "upgrade") {
      handleUpgradeSubscription(selectedPlan.priceId, quantity);
    } else {
      handleDowngradeSubscription(selectedPlan.priceId, quantity);
    }
  };

  const handleConfirmSubmit = () => {
    if (!selectedPlan) return;

    const quantity = 1;

    if (modalType === "upgrade") {
      handleUpgradeSubscription(selectedPlan.priceId, quantity);
    } else {
      handleDowngradeSubscription(selectedPlan.priceId, quantity);
    }

    setIsConfirmModalOpen(false);
  };

  const getActionButton = (plan) => {
    if (!subscriptionDetails) {
      return (
        <button
          className="plan-action-button upgrade"
          onClick={() => handlePlanAction(plan, "upgrade")}
        >
          <FaArrowUp /> Subscribe Now
        </button>
      );
    }

    const isCurrentPlan = subscriptionDetails.priceId === plan.priceId;
    const currentPlanLevel = getPlanLevel(subscriptionDetails.planName);
    const planLevel = getPlanLevel(plan.name);

    // console.log("Button Logic Debug:", {
    //   planName: plan.name,
    //   currentPlan: subscriptionDetails.planName,
    //   isCurrentPlan,
    //   currentPlanLevel,
    //   planLevel,
    // });

    if (isCurrentPlan) {
      return (
        <button
          className="plan-action-button current"
          onClick={() => setIsCancelModalOpen(true)}
        >
          <FaTimes /> Cancel Plan
        </button>
      );
    }

    if (!subscriptionDetails.planName || currentPlanLevel === 0) {
      return (
        <button
          className="plan-action-button upgrade"
          onClick={() => handlePlanAction(plan, "upgrade")}
        >
          <FaArrowUp /> Subscribe Now
        </button>
      );
    }

    if (planLevel < currentPlanLevel) {
      return (
        <button
          className="plan-action-button downgrade"
          onClick={() => handlePlanAction(plan, "downgrade")}
        >
          <FaArrowDown /> Downgrade Plan
        </button>
      );
    } else if (planLevel > currentPlanLevel) {
      return (
        <button
          className="plan-action-button upgrade"
          onClick={() => handlePlanAction(plan, "upgrade")}
        >
          <FaArrowUp /> Upgrade Plan
        </button>
      );
    }

    return null;
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    let date;
    if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp.toString().length === 10) {
      date = new Date(timestamp * 1000);
    } else if (timestamp.toString().length === 13) {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCardClassName = (plan) => {
    if (!subscriptionDetails) return "";
    if (subscriptionDetails.priceId === plan.priceId) return "active-plan";
    if (plan.price < subscriptionDetails.price) return "downgrade-plan";
    if (plan.price > subscriptionDetails.price) return "upgrade-plan";
    return "";
  };

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "number",
      key: "number",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Date",
      dataIndex: "created",
      key: "created",
      render: (timestamp) => formatDate(timestamp),
    },
    {
      title: "Amount",
      dataIndex: "total",
      key: "total",
      render: (amount, record) => formatCurrency(amount, record.currency),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span className={`invoice-status ${status}`}>
          {status === "paid" && <FaCheck style={{ fontSize: "10px" }} />}
          {/* {status === 'open' && <FaTimes style={{ fontSize: '10px' }} />} */}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "Description",
      dataIndex: ["lines", "data"],
      key: "description",
      render: (lines) => lines?.[0]?.description || "N/A",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {record.status === "open" ? (
            <Button
              type="primary"
              onClick={() => window.open(record.hosted_invoice_url, "_blank")}
              style={{
                background: "#8a3ef4",
                borderColor: "#8a3ef4",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "6px",
                fontSize: "13px",
                transition: "all 0.2s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#6b2fd9";
                e.currentTarget.style.borderColor = "#6b2fd9";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#8a3ef4";
                e.currentTarget.style.borderColor = "#8a3ef4";
              }}
            >
              Pay Now
            </Button>
          ) : (
            <Button
              className="download-button"
              icon={<FaDownload />}
              onClick={() => window.open(record.invoice_pdf, "_blank")}
            >
              Download PDF
            </Button>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetchSubscriptionDetails();
    fetchInvoices();
    checkLicenseLimit();
  }, []);

  return (
    <>
      {isModalOpen && (
        <SubscribeSuccessfulModal
          isOpen={isModalOpen}
          setOpen={setIsModalOpen}
        />
      )}
      <QuantityModal
        isOpen={isQuantityModalOpen}
        onClose={() => setIsQuantityModalOpen(false)}
        onSubmit={handleQuantitySubmit}
        type={modalType}
        currentQuantity={subscriptionDetails?.quantity || 1}
      />
      <ModelWrapper
        title={`${
          modalType === "upgrade" ? "Upgrade" : "Downgrade"
        } Subscription`}
        open={isConfirmModalOpen}
        onCancel={() => setIsConfirmModalOpen(false)}
        onOk={handleConfirmSubmit}
        okText={modalType === "upgrade" ? "Yes, Upgrade" : "Yes, Downgrade"}
        cancelText="Cancel"
        okButtonProps={{
          style: {
            background:
              modalType === "upgrade"
                ? "var(--success-gradient)"
                : "var(--error-gradient)",
            borderColor:
              modalType === "upgrade"
                ? "var(--success-color)"
                : "var(--error-color)",
            color: "white",
          },
        }}
        cancelButtonProps={{
          style: {
            background: "var(--primary-bg)",
            borderColor: "var(--border-color)",
            color: "var(--primary-text-color)",
          },
        }}
        className="confirm-subscription-modal"
      >
        <div className="modal-content">
          <div
            className="confirm-icon"
            style={{
              background:
                modalType === "upgrade"
                  ? "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)"
                  : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)",
              borderColor:
                modalType === "upgrade"
                  ? "rgba(16, 185, 129, 0.2)"
                  : "rgba(239, 68, 68, 0.2)",
            }}
          >
            {modalType === "upgrade" ? (
              <FaArrowUp
                style={{ fontSize: "24px", color: "var(--success-color)" }}
              />
            ) : (
              <FaArrowDown
                style={{ fontSize: "24px", color: "var(--error-color)" }}
              />
            )}
          </div>
          <p className="modal-message">
            {modalType === "upgrade"
              ? `Are you sure you want to upgrade to the ${selectedPlan?.name} plan?`
              : `Are you sure you want to downgrade to the ${selectedPlan?.name} plan?`}
            <br />
            <strong>This will take effect immediately.</strong>
          </p>
        </div>
      </ModelWrapper>
      <ModelWrapper
        title="Cancel Subscription"
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        onOk={() => {
          handleCancelSubscription();
          setIsCancelModalOpen(false);
        }}
        okText="Yes, Cancel Plan"
        cancelText="No, Keep Plan"
        okButtonProps={{
          style: {
            background: "var(--error-gradient)",
            borderColor: "var(--error-color)",
            color: "white",
          },
        }}
        cancelButtonProps={{
          style: {
            background: "var(--primary-bg)",
            borderColor: "var(--border-color)",
            color: "var(--primary-text-color)",
          },
        }}
        className="cancel-subscription-modal"
      >
        <div className="modal-content">
          <div className="warning-icon">
            <FaTimes
              style={{ fontSize: "24px", color: "var(--error-color)" }}
            />
          </div>
          <p className="modal-message">
            Are you sure you want to cancel your current subscription plan?
            <br />
            <strong>This action cannot be undone.</strong>
          </p>
        </div>
      </ModelWrapper>
      <MySubscriptionPageWrapper>
        <Row className="w-100 d-flex justify-content-center font-bold">
          <MainHeading>Your Subscription Plan</MainHeading>
        </Row>
        <Row className="w-100 d-flex justify-content-center font-medium mb-40">
          <SubHeading>
            Manage your subscription and explore available plans to optimize
            your medical education platform experience.
          </SubHeading>
        </Row>

        <Row
          justify="center"
          gutter={[32, 32]}
          className="w-100"
          style={{ marginTop: "40px" }}
        >
          {subscription
            .filter((item) => item.name !== "Enterprise")
            .map((item) => {
              const isActive = subscriptionDetails?.priceId === item.priceId;
              const currentPlanLevel = getPlanLevel(
                subscriptionDetails?.planName
              );
              const planLevel = getPlanLevel(item.name);
              const isHigherTier = planLevel > currentPlanLevel;
              const isLowerTier = planLevel < currentPlanLevel;

              let cardClass = "";
              if (isActive) {
                cardClass = "active-plan";
              } else if (isHigherTier) {
                cardClass = "upgrade-plan";
              } else if (isLowerTier) {
                cardClass = "downgrade-plan";
              }

              return (
                <Col
                  lg={8}
                  md={8}
                  sm={12}
                  xs={24}
                  key={item.id}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Card
                    className={cardClass}
                    style={{
                      width: "100%",
                      maxWidth: "380px",
                      minHeight: "500px",
                    }}
                  >
                    {isActive && (
                      <div className="active-badge">
                        <FaCheck /> Current Plan
                      </div>
                    )}

                    <div className="plan-header">
                      <div className="plan-icon">{item?.icon}</div>
                      <div className="plan-header-content">
                        <div className="plan-name">{item.name}</div>
                        <div className="plan-description">
                          {item.description}
                        </div>
                      </div>
                    </div>

                    <div className="plan-content">
                      <div className="price-expiry-row">
                        <div className="plan-price">
                          {item?.price ? `$${item.price}` : "Custom"}
                          <span className="billing-period">
                            {item?.billingLabel || "/month"}
                          </span>
                        </div>

                        {isActive && subscriptionDetails && (
                          <div className="expiry-date">
                            Expires {formatDate(subscriptionDetails.endDate)}
                          </div>
                        )}
                      </div>

                      <ul className="plan-features">
                        {item.features.map((feature, index) => (
                          <li key={index}>
                            <FaCheck /> {feature}
                          </li>
                        ))}
                      </ul>

                      {getActionButton(item)}
                    </div>
                  </Card>
                </Col>
              );
            })}
        </Row>

        <InvoiceTableWrapper>
          <Row className="w-100 d-flex justify-content-center font-bold mb-4">
            <MainHeading style={{ fontSize: "32px" }}>
              Invoice History
            </MainHeading>
          </Row>
          <Table
            columns={columns}
            dataSource={invoices}
            rowKey="id"
            pagination={{
              pageSize: 5,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} invoices`,
            }}
          />
        </InvoiceTableWrapper>
      </MySubscriptionPageWrapper>
    </>
  );
};

export default MySubscription;
